import React, { useState } from 'react'
import { Button, Pill, ConfirmDialog, Banner } from '../ui'

function relativeTime(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Lifecycle bar: ready / un-ready / share / close / reopen actions and state pills.
 * Renders at the top of the detail page.
 */
export function ReflectionLifecycleBar({
  reflection,
  isManager,
  isReport,
  onMarkReady,
  onShare,
  onClose,
  onReopen,
  hasCurrentManager = true,
}) {
  const [confirmClose, setConfirmClose] = useState(false)
  const [confirmReopen, setConfirmReopen] = useState(false)
  const [actionLoading, setActionLoading] = useState(null)

  const isClosed = !!reflection.closedAt
  const isReady = !!reflection.readyAt
  const isShared = !!reflection.sharedAt

  async function handleAction(fn, name) {
    setActionLoading(name)
    try { await fn() } finally { setActionLoading(null) }
  }

  // ── Closed state ──
  if (isClosed) {
    return (
      <>
        <Banner tone="info">
          <div style={styles.closedBanner}>
            <div style={styles.closedLeft}>
              <Pill tone="neutral">Closed {formatDate(reflection.closedAt)}</Pill>
              <span style={styles.closedText}>No further edits.</span>
            </div>
            {isManager && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmReopen(true)}
                loading={actionLoading === 'reopen'}
                loadingLabel="Reopening..."
              >
                Reopen
              </Button>
            )}
          </div>
        </Banner>
        <ConfirmDialog
          open={confirmReopen}
          title="Reopen reflection"
          message="Reopen this reflection? Both sides can edit again. Existing snapshots stay until you close again."
          confirmLabel="Reopen"
          onConfirm={() => { setConfirmReopen(false); handleAction(onReopen, 'reopen') }}
          onCancel={() => setConfirmReopen(false)}
        />
      </>
    )
  }

  // ── No manager ──
  if (!hasCurrentManager) {
    return (
      <div style={styles.bar}>
        <span style={styles.muted}>No current manager. Share and close are disabled until a new manager is assigned.</span>
      </div>
    )
  }

  // ── Employee view ──
  if (isReport) {
    return (
      <div style={styles.bar}>
        <div style={styles.barLeft}>
          {!isReady && <Pill tone="neutral">Draft</Pill>}
          {isReady && <Pill tone="info">Ready</Pill>}
          {isReady && isShared && <Pill tone="success">Manager shared</Pill>}
        </div>
        <div style={styles.barRight}>
          {!isReady && (
            <Button
              onClick={() => handleAction(onMarkReady, 'markReady')}
              loading={actionLoading === 'markReady'}
              loadingLabel="Marking ready..."
            >
              Mark ready
            </Button>
          )}
        </div>
      </div>
    )
  }

  // ── Manager view ──
  if (isManager) {
    return (
      <>
        <div style={styles.bar}>
          <div style={styles.barLeft}>
            {!isReady && <Pill tone="neutral">Draft</Pill>}
            {isReady && <Pill tone="info">Ready</Pill>}
            {isReady && !isShared && <Pill tone="neutral">Not yet shared</Pill>}
            {isReady && isShared && <Pill tone="success">Shared {relativeTime(reflection.sharedAt)}</Pill>}
          </div>
          <div style={styles.barRight}>
            {isReady && !isShared && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleAction(onShare, 'share')}
                loading={actionLoading === 'share'}
                loadingLabel="Sharing..."
              >
                Share observations
              </Button>
            )}
            {isReady && (
              <Button
                onClick={() => setConfirmClose(true)}
                loading={actionLoading === 'close'}
                loadingLabel="Closing..."
              >
                Close reflection
              </Button>
            )}
          </div>
        </div>
        <ConfirmDialog
          open={confirmClose}
          title="Close reflection"
          message="Close this reflection? Linked goals and selected sub-goals will be snapshotted. It becomes read-only. You can reopen later."
          confirmLabel="Close reflection"
          onConfirm={() => { setConfirmClose(false); handleAction(onClose, 'close') }}
          onCancel={() => setConfirmClose(false)}
        />
      </>
    )
  }

  return null
}

const styles = {
  bar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    flexWrap: 'wrap',
  },
  barLeft: { display: 'flex', alignItems: 'center', gap: '8px' },
  barRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  muted: { fontSize: '13px', color: 'var(--text-secondary)' },
  closedBanner: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
  },
  closedLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  closedText: {
    fontSize: '13px',
    color: 'var(--text-primary)',
  },
}
